import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import type { ReviewQueueCount } from '@zag/timetracking/common';

/**
 * Open-approvals badge counter (story 34-1, F14): exposes the number of
 * `needs_review` cases as a readonly signal for the "Freigaben" nav entry.
 *
 * Lives in the app (NOT in @zag/timetracking/ui) because the layout component
 * consumes it eagerly — importing the timetracking UI library here would pull
 * it into the initial bundle. The layout drives the refresh cadence (init +
 * gentle poll while the nav is visible); approve actions call `refresh()`
 * directly so the badge reacts immediately.
 */
@Injectable({ providedIn: 'root' })
export class ApprovalsCountService {
  private readonly http = inject(HttpClient);

  private readonly _count = signal(0);
  /** Open `needs_review` cases — 0 hides the badge. */
  readonly count = this._count.asReadonly();

  /** Re-fetches the count; transient errors keep the last known value. */
  refresh(): void {
    this.http.get<ReviewQueueCount>('/api/v1/time-tracking/review-queue/count').subscribe({
      next: result => this._count.set(result.count),
      error: () => {
        // Transient poll error (offline, redeploy): keep the last value —
        // the next poll tick or approve action recovers automatically.
      },
    });
  }
}
